<script setup lang="ts">
import { ComputedRef, Ref, computed, ref } from 'vue'

import { useShiftCopyMutation } from '../../../shifts/composables/useShiftSchedule'
import { useScheduleCopyDialog } from '../../composables/useScheduleCopyDialog'

import { Site } from '../../../sites/models/site'

const { site } = defineProps<{
  site: Site
}>()

const { hide, isShown, dateFrom, dateTo, building, viewDuration } =
  useScheduleCopyDialog()

const { mutate: shiftCopy, isPending: isShiftCopyPending } =
  useShiftCopyMutation(() => site.id)
const copyPressed = () => {
  const sourceDateFrom = dateFrom.value
  const sourceDateTo = dateTo.value
  const targetDate = dateTo.value.plus({ days: 1 }).startOf('day')
  const buildingId = building.value?.id ?? null

  if (sourceDateFrom && sourceDateTo && targetDate) {
    shiftCopy(
      {
        form: { sourceDateFrom, sourceDateTo, targetDate, buildingId },
      },
      {
        onSuccess: () => {
          hide()
        },
      },
    )
  }
}

type CopyType = 'next'
const copyTypeOptions: ComputedRef<{ label: string; value: CopyType }[]> =
  computed(() => {
    const durationUnit = viewDuration.value.unit
    const durationCount = viewDuration.value.count
    const durationLabel =
      durationCount > 1
        ? `${durationCount}-${durationUnit} period`
        : durationUnit
    const copyToNextLabel = `Copy to next ${durationLabel}`
    return [{ label: copyToNextLabel, value: 'next' }]
  })
const selectedCopyType: Ref<CopyType> = ref('next')
</script>

<template>
  <Dialog
    :visible="isShown"
    header="Copy Shifts"
    dismissable-mask
    modal
    class="w-full max-w-xl md:w-1/2"
    @update:visible="hide"
  >
    <div class="m-1 flex flex-col gap-y-4">
      <div class="flex flex-col gap-4">
        <div
          v-for="option in copyTypeOptions"
          :key="option.value"
          class="flex items-center gap-2"
        >
          <RadioButton
            v-model="selectedCopyType"
            :input-id="`copy-type-${option.value}`"
            :value="option.value"
          />
          <label :for="`copy-type-${option.value}`">{{ option.label }}</label>
        </div>
      </div>
      <div class="flex justify-end space-x-2">
        <Button label="Cancel" text @click="hide" />
        <Button
          label="Copy"
          :loading="isShiftCopyPending"
          :pt:root:class="'w-fit self-end'"
          @click="copyPressed"
        />
      </div>
    </div>
  </Dialog>
</template>
